/**
 * CostFormDialog — New/Edit cho cost_item (Batch Cost master).
 *
 * Visible fields:
 *   1. cost_item_code (required, unique — disabled trong edit mode để tránh
 *      đổi business key nếu không cần thiết; user vẫn có thể đổi qua API trực tiếp)
 *   2. display_code (optional, hiển thị trên UI)
 *   3. cost_group_code (required, combobox load từ /bom-cost/options)
 *   4. cost_item_name_vi (required)
 *   5. cost_item_name_en (optional)
 *   6. calc_method (required, enum QTY_MT / MANUAL)
 *   7. unit_price + currency + price_unit
 *   8. valid_from / valid_to
 *   9. vendor_name / vendor_contract_no / vendor_note
 *   10. sort_order, description
 *   vendor_log read-only, hiển thị history.
 *
 * Hidden system fields KHÔNG render: id, is_status, created_*, updated_*.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import type {
  CostDetail,
  CostOptions,
  CostSavePayload,
} from '@/store/api/factoryCostApi'
import { TEXT_TRUNCATE } from '@/theme/responsive'

interface DraftState {
  cost_item_code: string
  display_code: string
  cost_group_code: string
  cost_item_name_vi: string
  cost_item_name_en: string
  calc_method: string
  unit_price: string
  currency: string
  price_unit: string
  valid_from: string
  valid_to: string
  vendor_name: string
  vendor_contract_no: string
  vendor_note: string
  sort_order: string
  description: string
  vendor_log: string
}

const EMPTY_DRAFT: DraftState = {
  cost_item_code: '',
  display_code: '',
  cost_group_code: '',
  cost_item_name_vi: '',
  cost_item_name_en: '',
  calc_method: 'QTY_MT',
  unit_price: '',
  currency: 'VND',
  price_unit: 'MT',
  valid_from: '',
  valid_to: '',
  vendor_name: '',
  vendor_contract_no: '',
  vendor_note: '',
  sort_order: '0',
  description: '',
  vendor_log: '',
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Skill `/convert-form` readonly bg standard — neutral gray.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const toIsoDate = (v: string | null | undefined): string => {
  if (!v) return ''
  return String(v).slice(0, 10)
}

const numStr = (n: number | null | undefined): string => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  return String(n)
}

function detailToDraft(detail: CostDetail | null): DraftState {
  if (!detail) return { ...EMPTY_DRAFT }
  return {
    cost_item_code: detail.cost_item_code ?? '',
    display_code: detail.display_code ?? '',
    cost_group_code: detail.cost_group_code ?? '',
    cost_item_name_vi: detail.cost_item_name_vi ?? '',
    cost_item_name_en: detail.cost_item_name_en ?? '',
    calc_method: detail.calc_method ?? 'QTY_MT',
    unit_price: numStr(detail.unit_price),
    currency: detail.currency ?? 'VND',
    price_unit: detail.price_unit ?? 'MT',
    valid_from: toIsoDate(detail.valid_from),
    valid_to: toIsoDate(detail.valid_to),
    vendor_name: detail.vendor_name ?? '',
    vendor_contract_no: detail.vendor_contract_no ?? '',
    vendor_note: detail.vendor_note ?? '',
    sort_order: numStr((detail.cost_item_sort_order as number | null) ?? 0),
    description: detail.description ?? '',
    vendor_log: detail.vendor_log ?? '',
  }
}

function draftToPayload(draft: DraftState): CostSavePayload {
  const parseNum = (s: string): number | null => {
    const t = s.trim()
    if (!t) return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }
  return {
    cost_item_code: draft.cost_item_code.trim(),
    display_code: draft.display_code.trim() || null,
    cost_group_code: draft.cost_group_code.trim(),
    cost_item_name_vi: draft.cost_item_name_vi.trim(),
    cost_item_name_en: draft.cost_item_name_en.trim() || null,
    calc_method: draft.calc_method.trim() || 'QTY_MT',
    unit_price: parseNum(draft.unit_price),
    currency: draft.currency.trim() || 'VND',
    price_unit: draft.price_unit.trim() || 'MT',
    valid_from: draft.valid_from || null,
    valid_to: draft.valid_to || null,
    vendor_name: draft.vendor_name.trim() || null,
    vendor_contract_no: draft.vendor_contract_no.trim() || null,
    vendor_note: draft.vendor_note.trim() || null,
    sort_order: parseNum(draft.sort_order) ?? 0,
    description: draft.description.trim() || null,
  }
}

interface CostFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  detail: CostDetail | null
  saving: boolean
  detailLoading?: boolean
  options: CostOptions | null
  optionsLoading: boolean
  /** Permission gating — false → ẩn Save + show read-only banner. */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: CostSavePayload) => void
}

export default function CostFormDialog({
  open,
  mode,
  detail,
  saving,
  detailLoading,
  options,
  optionsLoading,
  canEdit = true,
  onClose,
  onSave,
}: CostFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (mode === 'edit') {
      setDraft(detailToDraft(detail))
    } else {
      setDraft({ ...EMPTY_DRAFT })
    }
  }, [open, mode, detail])

  const codeError = touched && !draft.cost_item_code.trim()
  const groupError = touched && !draft.cost_group_code.trim()
  const nameViError = touched && !draft.cost_item_name_vi.trim()
  const calcMethodError = touched && !draft.calc_method.trim()
  const dateError =
    touched &&
    draft.valid_from &&
    draft.valid_to &&
    draft.valid_to < draft.valid_from

  const validationError = codeError
    ? 'Cost Item Code is required'
    : groupError
    ? 'Cost Group is required'
    : nameViError
    ? 'Cost Item Name (VI) is required'
    : calcMethodError
    ? 'Calc Method is required'
    : dateError
    ? 'Valid To must be >= Valid From'
    : ''

  const handleSave = () => {
    if (!canEdit) return
    setTouched(true)
    if (
      !draft.cost_item_code.trim() ||
      !draft.cost_group_code.trim() ||
      !draft.cost_item_name_vi.trim() ||
      !draft.calc_method.trim() ||
      (draft.valid_from && draft.valid_to && draft.valid_to < draft.valid_from)
    ) {
      return
    }
    onSave(draftToPayload(draft))
  }

  const costGroupOptions = options?.costGroups ?? []
  const calcMethodOptions = options?.calcMethods ?? ['QTY_MT', 'MANUAL']

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="subtitle1"
            fontWeight={800}
            noWrap
            title={mode === 'new' ? 'New Cost Item' : `Edit Cost Item — ${draft.cost_item_code || ''}`}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0 }}
          >
            {mode === 'new'
              ? 'New Cost Item'
              : `Edit Cost Item — ${draft.cost_item_code || ''}`}
          </Typography>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={saving}
              sx={titleBtnSx}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canEdit || saving || detailLoading}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : null}
              sx={titleBtnSx}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {detailLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {!canEdit ? (
              <Alert severity="info">Read-only — you don't have edit permission for this factory.</Alert>
            ) : null}
            {validationError ? <Alert severity="error">{validationError}</Alert> : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1.25,
              }}
            >
              <TextField
                label="Cost Item Code"
                value={draft.cost_item_code}
                onChange={(e) => setDraft({ ...draft, cost_item_code: e.target.value })}
                size="small"
                required
                disabled={mode === 'edit'}
                error={codeError}
                sx={{ minWidth: 0, ...compactInputSx, ...(mode === 'edit' ? readonlyFieldSx : {}) }}
              />
              <TextField
                label="Display Code"
                value={draft.display_code}
                onChange={(e) => setDraft({ ...draft, display_code: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <Autocomplete
                size="small"
                options={costGroupOptions}
                value={
                  costGroupOptions.find((g) => g.cost_group_code === draft.cost_group_code) ?? null
                }
                onChange={(_, v) => setDraft({ ...draft, cost_group_code: v?.cost_group_code ?? '' })}
                getOptionLabel={(o) => o.cost_group_name_en || o.cost_group_code}
                isOptionEqualToValue={(a, b) => a.cost_group_code === b.cost_group_code}
                loading={optionsLoading}
                sx={{ minWidth: 0, ...compactInputSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cost Group"
                    required
                    error={groupError}
                  />
                )}
              />

              <TextField
                label="Item Name (EN)"
                value={draft.cost_item_name_en}
                onChange={(e) => setDraft({ ...draft, cost_item_name_en: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Item Name (VI)"
                value={draft.cost_item_name_vi}
                onChange={(e) => setDraft({ ...draft, cost_item_name_vi: e.target.value })}
                size="small"
                required
                error={nameViError}
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <Autocomplete
                size="small"
                options={calcMethodOptions}
                value={draft.calc_method || null}
                onChange={(_, v) => setDraft({ ...draft, calc_method: v ?? '' })}
                getOptionLabel={(o) => o ?? ''}
                isOptionEqualToValue={(a, b) => a === b}
                sx={{ minWidth: 0, ...compactInputSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Calc Method"
                    required
                    error={calcMethodError}
                  />
                )}
              />

              <TextField
                label="Unit Price"
                value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                size="small"
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                sx={{ minWidth: 0, ...compactInputSx }}
                helperText={draft.calc_method === 'MANUAL' ? 'MANUAL: enter price per record' : undefined}
              />
              <TextField
                label="Currency"
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Price Unit"
                value={draft.price_unit}
                onChange={(e) => setDraft({ ...draft, price_unit: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">per</InputAdornment>,
                }}
              />

              <TextField
                label="Valid From"
                type="date"
                value={draft.valid_from}
                onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Valid To"
                type="date"
                value={draft.valid_to}
                onChange={(e) => setDraft({ ...draft, valid_to: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!dateError}
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Sort Order"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                size="small"
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                sx={{ minWidth: 0, ...compactInputSx }}
              />
            </Box>

            <Divider textAlign="left">
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                VENDOR
              </Typography>
            </Divider>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.25,
              }}
            >
              <TextField
                label="Vendor Name"
                value={draft.vendor_name}
                onChange={(e) => setDraft({ ...draft, vendor_name: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Vendor Contract No"
                value={draft.vendor_contract_no}
                onChange={(e) => setDraft({ ...draft, vendor_contract_no: e.target.value })}
                size="small"
                sx={{ minWidth: 0, ...compactInputSx }}
              />
              <TextField
                label="Vendor Note"
                value={draft.vendor_note}
                onChange={(e) => setDraft({ ...draft, vendor_note: e.target.value })}
                size="small"
                multiline
                minRows={2}
                sx={{ minWidth: 0, gridColumn: { xs: '1', sm: '1 / -1' }, ...compactInputSx }}
              />
              <TextField
                label="Description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                size="small"
                multiline
                minRows={2}
                sx={{ minWidth: 0, gridColumn: { xs: '1', sm: '1 / -1' }, ...compactInputSx }}
              />
              {mode === 'edit' && draft.vendor_log ? (
                <TextField
                  label="Vendor Log"
                  value={draft.vendor_log}
                  size="small"
                  multiline
                  minRows={3}
                  InputProps={{ readOnly: true }}
                  sx={{
                    minWidth: 0,
                    gridColumn: { xs: '1', sm: '1 / -1' },
                    ...compactInputSx,
                    ...readonlyFieldSx,
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      whiteSpace: 'pre-wrap',
                      color: 'text.secondary',
                      fontWeight: 600,
                    },
                  }}
                />
              ) : null}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
